/* ==========================================================================
   ZOMBIE SNACKS — PRODUCT PORTAL HERO (Phase 3A)
   Scoped to <zombie-portal-hero>. The slider itself is Taiga's native
   <gridy-slider> (global.js): scrolling, autoplay, arrows and the indicator
   state stay native and untouched. This element only:
     1. upgrades the native indicator dots (presentational in Taiga) into real
        buttons with labels that jump to a world;
     2. stops autoplay for visitors who prefer reduced motion, and (setting)
        ends autoplay for the visit as soon as the visitor interacts with
        the slider (dot, arrow, swipe / manual scroll);
     3. on pointer devices, drifts the ACTIVE world's flying-assets layer
        gently toward the pointer (±18px, ±1° in front, half that behind) by writing three custom
        properties on the world element — the easing itself is a CSS
        transition, so there is no animation loop. Touch devices and
        reduced-motion visitors get no pointer tracking.
     4. (Phase 6) preloads only the NEXT world: worlds 3+ render with
        loading="lazy"; whenever a world becomes active its neighbours are
        promoted to eager, as is any world the visitor points at (dot hover /
        focus, arrow hover), so the next slide is cached before it arrives.
   ========================================================================== */
(function () {
  if (customElements.get('zombie-portal-hero')) return;

  const PARALLAX_PX = 18;   // max X / Y drift of the front flying layer (back layer = half)
  const PARALLAX_DEG = 1;   // max rotation of the flying layer

  class ZombiePortalHero extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('gridy-slider');
      if (!this.slider) return;
      const ready = () => (typeof this.slider.scrollToSlide === 'function' && this.slider.config);
      if (ready()) this.upgrade();
      else customElements.whenDefined('gridy-slider').then(() => ready() && this.upgrade());
    }

    upgrade() {
      const slider = this.slider;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion && slider.config.autoplay) this.stopAutoplay();

      // Any interaction ends auto-rotate for this visit (Taiga only pauses on hover)
      this.stopOnInteraction = slider.dataset.stopOnInteraction !== 'false';
      if (this.stopOnInteraction && slider.config.autoplay) {
        const stop = () => this.stopAutoplay();
        const track = slider.querySelector('.gridy-track');
        if (track) {
          track.addEventListener('pointerdown', stop, { passive: true });
          track.addEventListener('touchstart', stop, { passive: true });
          track.addEventListener('wheel', stop, { passive: true });
        }
        slider.querySelectorAll('[data-gridy-arrow]').forEach((arrow) => arrow.addEventListener('click', stop));
      }

      const names = Array.from(this.querySelectorAll('.zombie-portal__world')).map((w) => w.dataset.world || '');
      this.querySelectorAll('[data-indicator-dot]').forEach((dot, index) => {
        dot.removeAttribute('aria-hidden');
        dot.removeAttribute('role');
        dot.tabIndex = 0;
        dot.setAttribute('aria-label', names[index] ? 'Show ' + names[index] : 'Show world ' + (index + 1));
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.stopOnInteraction) this.stopAutoplay();
          slider.config.current_slide = index;
          slider.scrollToSlide(index);
          if (typeof slider.setActiveSlide === 'function') slider.setActiveSlide(slider.slides[index]);
        });
      });

      this.setupPreload();

      // Flying-asset parallax: pointer devices only, never under reduced motion
      const pointerFine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (pointerFine && !reduceMotion && this.querySelector('.zombie-portal__flying')) {
        this.addEventListener('pointermove', this.onPointerMove.bind(this), { passive: true });
        this.addEventListener('pointerleave', this.resetParallax.bind(this), { passive: true });
      }
    }

    setupPreload() {
      this.worlds = Array.from(this.querySelectorAll('.zombie-portal__world'));
      if (this.worlds.length < 3) return;
      const activeIndex = () => Math.max(0, this.worlds.findIndex((w) => w.classList.contains('active')));
      const neighbours = () => {
        const i = activeIndex();
        this.preloadWorld(i + 1);
        this.preloadWorld(i - 1);
      };
      neighbours();
      // Taiga's slider toggles .active on the world it lands on
      const observer = new MutationObserver(neighbours);
      this.worlds.forEach((w) => observer.observe(w, { attributes: true, attributeFilter: ['class'] }));
      this.querySelectorAll('[data-indicator-dot]').forEach((dot, index) => {
        const warm = () => this.preloadWorld(index);
        dot.addEventListener('pointerenter', warm, { passive: true });
        dot.addEventListener('focus', warm);
      });
      this.querySelectorAll('[data-gridy-arrow]').forEach((arrow) => {
        arrow.addEventListener('pointerenter', neighbours, { passive: true });
        arrow.addEventListener('focus', neighbours);
      });
    }

    preloadWorld(index) {
      const count = this.worlds.length;
      const world = this.worlds[((index % count) + count) % count];
      if (!world || world.dataset.preloaded) return;
      world.dataset.preloaded = 'true';
      world.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    stopAutoplay() {
      const slider = this.slider;
      if (!slider.config.autoplay) return;
      if (typeof slider.clearAutoplayTimer === 'function') slider.clearAutoplayTimer();
      slider.config.autoplay = false;
      // Taiga registered its own bound mouseleave → autoplayGridy listener at
      // construction, which restarts the timer. Our listener is added later,
      // so it runs after Taiga's and clears the timer again.
      if (!this.autoplayGuard) {
        this.autoplayGuard = () => { if (typeof slider.clearAutoplayTimer === 'function') slider.clearAutoplayTimer(); };
        slider.addEventListener('mouseleave', this.autoplayGuard);
        slider.addEventListener('touchleave', this.autoplayGuard);
      }
    }

    onPointerMove(event) {
      const world = this.querySelector('.zombie-portal__world.active');
      if (!world || !world.querySelector('.zombie-portal__flying')) return;
      const rect = world.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const nx = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
      const ny = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
      if (this.parallaxWorld && this.parallaxWorld !== world) this.clearWorld(this.parallaxWorld);
      this.parallaxWorld = world;
      world.style.setProperty('--fly-x', (nx * PARALLAX_PX).toFixed(1) + 'px');
      world.style.setProperty('--fly-y', (ny * PARALLAX_PX).toFixed(1) + 'px');
      world.style.setProperty('--fly-r', (nx * PARALLAX_DEG).toFixed(2) + 'deg');
    }

    resetParallax() {
      if (this.parallaxWorld) this.clearWorld(this.parallaxWorld);
      this.parallaxWorld = null;
    }

    clearWorld(world) {
      world.style.removeProperty('--fly-x');
      world.style.removeProperty('--fly-y');
      world.style.removeProperty('--fly-r');
    }
  }

  customElements.define('zombie-portal-hero', ZombiePortalHero);
})();
